import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";
import { useNavigationPermissions } from "@/hooks/useNavigationPermissions";  
import { getMarketingUrl } from "@/utils/domains";
import { SubscriptionService } from "@/services/subscriptionService";

export const useAuthForm = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, signIn, signUp, userRole } = useAuth();
  const { canAccessSuperUserRoutes } = useNavigationPermissions();
  
  // Determine redirect path based on user role (keep existing logic)
  const getRedirectPath = async () => {
    if (canAccessSuperUserRoutes) {
      console.log("Auth: User is a superadmin, redirecting to /superadmin");
      return "/superadmin";
    }
    
    // Check if user is HiveShare-only
    if (user?.user_metadata?.organization_id && (userRole === 'bizadmin' || userRole === 'admin' || userRole === 'staff')) {
      try {
        const accessInfo = await SubscriptionService.getOrganizationAccess(user.user_metadata.organization_id);
        if (accessInfo.status === 'active-hiveshare') {
          console.log("Auth: HiveShare-only user, redirecting to /admin/hiveshare");
          return "/admin/hiveshare";
        }
      } catch (error) {
        console.error("Auth: Error checking subscription status:", error);
        // Fall through to default routing if subscription check fails
      }
    }
    
    // For regular admin/bizadmin users
    return location.state?.from || "/dashboard";
  };
  
  // Get initial from value from location state or query params
  const [from, setFrom] = useState(() => {
    const queryFrom = searchParams.get('from');
    return queryFrom || location.state?.from || "/dashboard";
  });
  
  // Update redirect destination when user role changes
  useEffect(() => {
    if (user && userRole) {
      const updateRedirectPath = async () => {
        const path = await getRedirectPath();
        setFrom(path);
      };
      updateRedirectPath();
    }
  }, [user, userRole, canAccessSuperUserRoutes, location.state?.from]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { error } = await signIn(email, password);
      if (error) throw error;
      toast.success("Signed in successfully");
    } catch (error: any) {
      toast.error("Sign in failed", {
        description: error.message || "Please check your credentials and try again"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    await doSignUp();
  };


  // Old function for sign-up logic without needing the event (keeping for backward compatibility)
  const doSignUp = async () => {
    setLoading(true);

    if (!firstName || !lastName || !companyName) {
      toast.error("Missing required fields", {
        description: "Please fill in all required fields."
      });
      setLoading(false);
      return;
    }
    if (!selectedPriceId) {
      toast.error("Please select a plan.");
      setLoading(false);
      return;
    }

    try {
      console.log("Starting signup process...");
      console.log("Selected price ID:", selectedPriceId);
      const result = await signUp(email, password, "bizadmin", companyName, firstName, lastName, selectedPriceId);
      if (result.error) {
        console.error("Signup error:", result.error);
        if (result.error.message?.includes("organization creation failed")) {
          toast.error("Account created but organization setup failed", {
            description: "Your account was created but we couldn't set up your organization. Please contact support."
          });
          navigate('/email-verification', { state: { email, organizationFailed: true } });
          return;
        }
        throw result.error;
      }
      if ('organization' in result && (result.organization as { id?: string })?.id) {
        console.log("Organization created successfully with ID:", (result.organization as { id: string }).id);
      }
      if ('profile' in result && (result.profile as { organization_id?: string })?.organization_id) {
        console.log("User profile created with organization ID:", (result.profile as { organization_id: string }).organization_id);
      }
      toast.success("Account created", {
        description: "Please check your email for verification instructions"
      });
      navigate('/email-verification', { state: { email } });
    } catch (error: any) {
      console.error("Signup process error:", error);
      let errorMessage = error.message || "Please try again";
      if (errorMessage.includes("already registered")) {
        errorMessage = "This email is already registered. Please use a different email or try signing in.";
      } else if (errorMessage.includes("organization")) {
        errorMessage = "We couldn't create your organization. Please try again or contact support.";
      } else if (errorMessage.includes("password")) {
        errorMessage = "Password doesn't meet requirements. Please use at least 8 characters with a mix of letters and numbers.";
      }
      toast.error("Sign up failed", {
        description: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setResetError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setResetError(null);

    try {
      // Use marketing domain for password reset redirect
      const resetUrl = import.meta.env.DEV 
        ? `${window.location.origin}/reset-password`
        : `${getMarketingUrl()}/reset-password`;
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: resetUrl
      });
      
      if (error) throw error;
      
      setResetSent(true);
      toast.success("Reset link sent", {
        description: "Check your email for a password reset link."
      });
    } catch (error: any) {
      setResetError(error.message || "Failed to send reset link. Please try again.");
      toast.error("Password reset failed", {
        description: error.message || "Please check your email and try again"
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    companyName,
    setCompanyName,
    selectedPriceId,
    setSelectedPriceId,
    loading,
    resetMode,
    setResetMode,
    resetSent,
    resetError,
    handleSignIn,
    handleSignUp,
    doSignUp,
    handlePasswordReset,
    user,
    from
  };
};
